/**
 * WHAT THE CABINET NEEDS FROM FIREBASE AUTH, written as OUR contract rather than as the SDK's.
 *
 * WHY A PORT AND NOT A DIRECT IMPORT. Two reasons, and the second is the one that matters.
 *
 *   1. The fixture came first. `firebase`, the web SDK, was not a dependency when this was
 *      written; it is now (web/authors/package.json), and ./firebase-auth-client.ts is the one
 *      file that imports it. The port is why nothing else in this feature had to change.
 *
 *   2. Far more importantly, every screen in this feature would otherwise depend on the SDK's own
 *      error vocabulary — `auth/popup-blocked` and its siblings — and THOSE STRINGS APPEAR NOWHERE
 *      IN THE CORPUS. A blocked popup never reaches our API at all — it is a client-side SDK error
 *      with no HTTP status — and all the corpus says of it is that "popups are sometimes blocked"
 *      (docs/design/adr/0007-web-app-stack.md). With the mapping from the SDK's strings to our
 *      states in ONE table (`MAPPED_SDK_ERRORS`, ./create-auth-client.ts), confirming it against
 *      the real SDK was one review of one constant. In six components it would not have been.
 *
 * SO: the interface below is the whole of what the cabinet may ask of Firebase, and
 * ./create-auth-client.ts is the single place an implementation is chosen.
 */

/**
 * The three ruled providers.
 *
 * LOCKED D29 (docs/design/00-scope-v1.md) rules "sign-in v1: Apple + Google + e-mail magic
 * link" — THREE, not two. The owner's own sign-in drawing shows Apple and Google only, and the
 * prototype contains no sign-in pixel at all, so the drawing is intent delivered directly and not
 * a scope cut (decision-log row 117: "the prototype is INTENT, NOT SPEC"). The magic link is easy
 * to lose precisely because the drawing does not show it, which is why it is a member of this type
 * rather than a comment.
 */
export type PopupProvider = 'apple' | 'google';

/**
 * A person, as this feature sees one. PLAIN DATA — no methods — so the session reducer can be pure
 * and testable and so nothing downstream can accidentally hold on to a live SDK object.
 */
export interface AuthIdentity {
  /**
   * The Firebase uid. THE ONE FACT THIS WHOLE FEATURE PROTECTS: linking an anonymous account to a
   * real provider KEEPS THIS VALUE (docs/design/research/26-authors-identity-application-research.md).
   * Proved against the Firebase Auth emulator on 2026-08-27: linking returned the same localId.
   */
  readonly uid: string;
  /**
   * True while the person is signed in anonymously.
   *
   * G30 requires an author to have signed in with a NON-ANONYMOUS provider, but that is "a property
   * of the TOKEN, not of the account, and it is enforced at APPLICATION-SUBMIT time, not at
   * sign-in". The remedy is a LINK, never a refusal and never a second account.
   */
  readonly isAnonymous: boolean;
  /**
   * What the account is linked to, in the adapter's words. Rendered - and read by one screen: the
   * payout method's re-sign-in picks its popup provider from these words
   * (`routes/payments/reauthenticate.ts`), and a word it does not know offers no popup.
   */
  readonly providerLabels: readonly string[];
  readonly displayName: string | null;
  readonly email: string | null;
}

/** An identity plus the one thing only a live session can do: produce a bearer token. */
export interface AuthSession {
  readonly identity: AuthIdentity;
  /**
   * The Firebase ID token to put in the `Authorization` header.
   *
   * `forceRefresh` exists for exactly one caller: a bare 401 (`Bearer realm="moonx"` with no
   * `error="invalid_token"`) means no credential was presented, which can be our own staleness, and
   * is worth one forced refresh before giving up (docs/design/07-api/shared.yaml).
   */
  readonly getIdToken: (forceRefresh?: boolean) => Promise<string>;
}

/**
 * THE BROWSER REFUSED THE POPUP. This is the first of the four named failure states, and the only
 * one that never touches our API: `signInWithPopup` throws before any request to `authors-api`
 * exists, so there is no status and no `error_code` to render.
 */
export class PopupBlockedError extends Error {
  public constructor(cause?: unknown) {
    super('The browser blocked the sign-in window.');
    this.name = 'PopupBlockedError';
    this.cause = cause;
  }
}

/**
 * The person closed the popup, or backed out at the provider. NOT AN ERROR — the screen returns
 * to idle silently. An apologetic red banner for someone who changed their mind is noise.
 */
export class PopupCancelledError extends Error {
  public constructor(cause?: unknown) {
    super('Sign-in was cancelled.');
    this.name = 'PopupCancelledError';
    this.cause = cause;
  }
}

/** Apple or Google could not be reached. The SDK's own network failure; again no status of ours. */
export class ProviderUnreachableError extends Error {
  public constructor(cause?: unknown) {
    super('The sign-in provider could not be reached.');
    this.name = 'ProviderUnreachableError';
    this.cause = cause;
  }
}

/**
 * A link was attempted onto an account that already has that provider, or the provider account is
 * already attached to a different MoonX account. Rendered as an explanation, never as a new sign-up.
 */
export class ProviderAlreadyLinkedError extends Error {
  public constructor(cause?: unknown) {
    super('That account is already connected.');
    this.name = 'ProviderAlreadyLinkedError';
    this.cause = cause;
  }
}

/** Anything the adapter could not classify. Rendered as "something went wrong", never swallowed. */
export class AuthUnavailableError extends Error {
  public constructor(message: string, cause?: unknown) {
    super(message);
    this.name = 'AuthUnavailableError';
    this.cause = cause;
  }
}

export interface AuthClient {
  /** The restored session, if the SDK had one for THIS ORIGIN. Sessions are per-origin (adr/0002). */
  readonly currentSession: () => AuthSession | null;
  /** Fires once with the restored session (or null) and again on every change. Returns an unsubscribe. */
  readonly subscribe: (listener: (session: AuthSession | null) => void) => () => void;

  /** Cold start, popup first. Throws `PopupBlockedError` when the browser refuses the window. */
  readonly signInWithPopup: (provider: PopupProvider) => Promise<AuthSession>;

  /** The hand-off. Signs in with the custom token leg two returned. */
  readonly signInWithCustomToken: (customToken: string) => Promise<AuthSession>;

  /**
   * G30's remedy. Attaches a real provider to the CURRENT account and keeps its uid.
   *
   * An implementation that returns a different uid is a bug severe enough to fail loudly:
   * ../session/link-anonymous.ts checks it and refuses the result rather than letting a second
   * account quietly exist.
   */
  readonly linkWithPopup: (provider: PopupProvider) => Promise<AuthSession>;

  /** Sends the e-mail magic link. LOCKED D29's third provider. */
  readonly sendMagicLink: (email: string) => Promise<void>;
  /** True when the current URL is a magic-link callback. */
  readonly isMagicLinkUrl: (url: string) => boolean;
  /** Finishes a magic-link sign-in. `email` is asked for again when the device changed. */
  readonly completeMagicLink: (url: string, email: string) => Promise<AuthSession>;

  /**
   * Sign-out, which is entirely local.
   *
   * `authors-api` HAS NO SIGN-OUT OPERATION — verified against the generated types; `admin-api`
   * has `adminSignOut` and this surface has nothing of the kind. That is correct rather than
   * missing: the cabinet is bearer-only and holds no server-side session (adr/0002), so signing
   * out is the SDK's local `signOut()` plus dropping our cached session. The corpus never says so
   * in as many words, which is reported as a gap.
   */
  readonly signOut: () => Promise<void>;
}
